import type { InputBaseModel, NodeBaseModel } from '../models';
import InputIds from './InputIds';
import InputMetadata from './InputMetadata';
import NodeIds from './NodeIds';
import NodeUtils from './NodeUtils';
import ClassificationInputLayoutUtils from './ClassificationInputLayoutUtils';

export interface SectionDetail {
    id: string;
    label: string;
    value: string;
}

export interface SectionDetails {
    classifications: SectionDetail[];
    territories: SectionDetail[];
}

function detail(id: string, label: string, input?: InputBaseModel): SectionDetail {
    const formattedValue = input?.formattedValue;
    const values = (Array.isArray(formattedValue) ? formattedValue : [formattedValue])
        .map(value => String(value ?? '').trim())
        .filter(Boolean);

    return { id, label: input?.name ?? label, value: values.join(', ') || 'Not specified' };
}

export function getSectionDetails(
    nodes: readonly NodeBaseModel[],
    sectionId: string,
    layerId?: string
): SectionDetails {
    const inputsFor = (nodeId: string) =>
        nodes
            .filter(node => node.id === nodeId)
            .flatMap(
                node => NodeUtils.getSections(node, layerId).find(section => section.id === sectionId)?.inputs ?? []
            );
    const classifications = inputsFor(NodeIds.Classifications);
    const territories = inputsFor(NodeIds.Countries);
    const rows = (nodeId: string, ids: string[], inputs: InputBaseModel[]) =>
        ids.map(id =>
            detail(
                id,
                InputMetadata[nodeId][id].name,
                inputs.find(input => input.id === id)
            )
        );
    const additionalClassifications = ClassificationInputLayoutUtils.getAdditionalClassificationInputs(classifications);

    return {
        classifications: [
            ...rows(
                NodeIds.Classifications,
                [InputIds.Classifications.MainClass, InputIds.Classifications.Class, InputIds.Classifications.SubClass],
                classifications
            ),
            ...additionalClassifications.map(input => detail(input.id, input.name, input))
        ],
        territories: rows(
            NodeIds.Countries,
            [InputIds.Countries.CountryGroups, InputIds.Countries.Countries],
            territories
        )
    };
}
